import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import MUIGrid from "@mui/material/Grid";
import MUICard from "@mui/material/Card";
import MUITypography from "@mui/material/Typography";
import MUIButton from "@mui/material/Button";
import MUIPaper from "@mui/material/Paper";
import MUIBackdrop from "@mui/material/Backdrop";
import MUIAlert from "@mui/material/Alert";
import MUIFileDownloadIcon from "@mui/icons-material/FileDownload";
import MUICachedIcon from "@mui/icons-material/Cached";

import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";
import Progress from "./Progress";
import { jsPDF } from "jspdf";
import api from "../Seating/Security/api";
import File from "../Utility/File";

export default function FileViewer() {
  const params = useParams();
  const history = useHistory();
  const [fileUrl, setFileUrl] = useState(null);
  const [fileError, setFileError] = useState(null);
  const isFileTransfer = Number.isInteger(Number(params.id)) === false;
  const fileId = isFileTransfer ? null : params.id;
  const transferId = isFileTransfer ? params.id : null;
  const fileName = params.filename;
  const [uploadedFile, setUploadedFile] = useState(null);
  const retryState = useState(0);
  const retryCount = retryState[0];
  const setRetryCount = retryState[1];
  // Set the document title
  document.title = fileName || "File Viewer";

  function handleRetry() {
    setFileError(null);
    setFileUrl(null);
    setUploadedFile(null);
    setRetryCount(retryCount + 1);
  }

  function handleDownload() {
    if (!fileUrl || !fileName) {
      NotistackEnqueueSnackbar(`Download Failed\n${fileError}`, {
        variant: "error",
        style: { whiteSpace: "pre-line" },
      });
      return;
    }
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = `sp-${fileName}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async function convertImageToPDF(res, mimeType) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const imageUrl = URL.createObjectURL(new Blob([res], { type: mimeType }));
      img.onload = function () {
        try {
          const doc = new jsPDF({
            orientation:
              img.naturalWidth > img.naturalHeight ? "landscape" : "portrait",
            unit: "px",
            format: [img.naturalWidth, img.naturalHeight],
          });
          doc.addImage(
            img,
            mimeType.split("/")[1].toUpperCase(),
            0,
            0,
            img.naturalWidth,
            img.naturalHeight,
          );
          const pdfBlob = doc.output("blob");
          resolve(pdfBlob);
        } catch (error) {
          reject(error);
        } finally {
          URL.revokeObjectURL(imageUrl);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);
        reject(new Error("Failed to load image for PDF conversion."));
      };
      img.src = imageUrl;
    });
  }

  function getMimeType(fileName) {
    if (!fileName) {
      return null;
    }
    const ext = fileName.split(".").pop().toLowerCase();
    switch (ext) {
      case "pdf":
        return "application/pdf";
      case "jpg":
      case "jpeg":
        return "image/jpeg";
      case "png":
        return "image/png";
      case "gif":
        return "image/gif";
      case "bmp":
        return "image/bmp";
      case "webp":
        return "image/webp";
      case "doc":
        return "application/msword";
      case "docx":
        return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      case "xls":
        return "application/vnd.ms-excel";
      case "xlsx":
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      case "csv":
        return "text/csv";
      case "txt":
        return "text/plain";
      default:
        return null;
    }
  }

  useEffect(() => {
    let fileObjectUrl = null;
    let pollingInterval = null;
    let isLoading = false;
    let isCancelled = false;

    const fetchFile = async () => {
      if (isLoading) {
        return;
      }

      isLoading = true;

      try {
        let res;
        if (fileId) {
          res = await api.send("GET", `/files/${fileId}`);
        } else {
          res = await File.unstashFile(transferId);
          if (!res) {
            return;
          }
          window.clearInterval(pollingInterval);
        }

        if (res) {
          const mimeType = res.type ?? getMimeType(fileName);
          let file;
          if (mimeType === "application/pdf") {
            file = new Blob([res], { type: mimeType });
          } else if (mimeType.startsWith("image/")) {
            file = await convertImageToPDF(res, mimeType);
            setUploadedFile(file);
          } else if (mimeType === "text/plain") {
            // Convert text to PDF
            const text = await new Response(res).text();
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 10;
            const lineHeight = 8; // Adjust as needed
            doc.setFontSize(12);
            // Split text into lines and wrap them to fit the page width
            const lines = text
              .split("\n")
              .flatMap((line) =>
                doc.splitTextToSize(line, pageWidth - 2 * margin),
              );

            let cursorY = margin;
            lines.forEach((line, idx) => {
              if (cursorY + lineHeight > pageHeight - margin) {
                doc.addPage();
                cursorY = margin;
              }
              doc.text(line, margin, cursorY);
              cursorY += lineHeight;
            });

            file = doc.output("blob");
          } else {
            // For unsupported types, just create a blob
            file = new Blob([res], { type: mimeType });
          }

          if (isCancelled) {
            return;
          }

          setUploadedFile(file);
          fileObjectUrl = window.URL.createObjectURL(file);
          let filenameWithExtension = fileName.replace(/\.[^/.]+$/, ".pdf");
          let url = `${fileObjectUrl}#${File.sanitizeUrlFilename(filenameWithExtension)}`;

          setFileUrl(url);
        }
      } catch (err) {
        if (!isCancelled) {
          window.clearInterval(pollingInterval);
          setFileUrl(null);
          setUploadedFile(null);
          if (err.message === "No session guid available") {
            history.push("/load/");
          }
          if (err.message.includes("404")) {
            setFileError(
              "File not found, return to previous page and upload your file again.",
            );
          } else if (err.message.includes("204")) {
            setFileError(
              "File is empty, return to previous page and upload your file again.",
            );
          } else if (err.message === "Generated file failed") {
            setFileError(
              "File generation failed. Return to the previous page and try again.",
            );
          } else {
            setFileError("Unknown error occurred, please try again later.");
          }
        }
      } finally {
        isLoading = false;
      }
    };

    fetchFile();
    if (transferId) {
      pollingInterval = window.setInterval(fetchFile, 250);
    }

    return () => {
      isCancelled = true;
      window.clearInterval(pollingInterval);
      if (fileObjectUrl) {
        window.URL.revokeObjectURL(fileObjectUrl);
      }
    };
  }, [fileId, fileName, transferId, retryCount]);
  // Use iframe for PDF viewing
  return (
    <MUIGrid
      container
      sx={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {fileError ? (
        <>
          <MUIGrid
            container
            sx={{ mt: 1, display: "flex", justifyContent: "center" }}
            spacing={2}
          >
            <MUICard
              sx={{
                height: "30%",
                width: "100%",
                maxWidth: "600px",
                padding: 2,
              }}
            >
              <MUIGrid
                container
                justifyContent="space-between"
                flexDirection="column"
              >
                <MUITypography
                  sx={{
                    fontSize: 20,
                    alignContent: "flex-start",
                    paddingBottom: 2,
                  }}
                  variant="h2"
                  color="textSecondary"
                >
                  {fileName}
                </MUITypography>
                <MUIAlert severity="error" sx={{ fontWeight: "bold" }}>
                  Cannot load file
                  <MUITypography variant="body2">{fileError}</MUITypography>
                </MUIAlert>
              </MUIGrid>
              <MUIGrid
                item
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                  flexDirection: "row",
                  paddingTop: 2,
                }}
              >
                {fileId && (
                  <MUIButton variant="contained" onClick={handleRetry}>
                    <MUICachedIcon sx={{ mr: 1 }} />
                    Retry
                  </MUIButton>
                )}
              </MUIGrid>
            </MUICard>
          </MUIGrid>
        </>
      ) : !fileUrl && !uploadedFile ? (
        // ...loading indicator...
        <MUIBackdrop open={true}>
          <MUIPaper sx={{ padding: 4, width: 400 }}>
            <Progress variant="indeterminate" message="Fetching data..." />
          </MUIPaper>
        </MUIBackdrop>
      ) : // ...existing viewer and download fallback...
      uploadedFile && ["application/pdf"].includes(uploadedFile.type) ? (
        <iframe
          title={`${fileName}`}
          style={{ border: "none", height: "100%", width: "100%" }}
          src={"/pdfjs/web/viewer.html?file=" + encodeURIComponent(fileUrl)}
          sandbox="allow-scripts allow-modals allow-same-origin allow-downloads"
        />
      ) : (
        <MUIGrid
          container
          sx={{ mt: 1, display: "flex", justifyContent: "center" }}
          spacing={2}
        >
          <MUICard
            sx={{ height: "30%", width: "100%", maxWidth: "600px", padding: 2 }}
          >
            <MUIGrid
              container
              justifyContent="space-between"
              flexDirection="column"
            >
              <MUITypography
                sx={{
                  fontSize: 20,
                  alignContent: "flex-start",
                  paddingBottom: 2,
                }}
                variant="h2"
                color="textSecondary"
              >
                {fileName}
              </MUITypography>
              <MUIAlert severity="info" sx={{ fontWeight: "bold" }}>
                Cannot preview file
                <MUITypography variant="body2">
                  This file type cannot be viewed directly. Please download the
                  file to view it.
                </MUITypography>
              </MUIAlert>
            </MUIGrid>
            <MUIGrid
              item
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                paddingTop: 2,
              }}
            >
              <MUIButton variant="contained" onClick={handleDownload}>
                <MUIFileDownloadIcon sx={{ mr: 1 }} />
                Download File
              </MUIButton>
            </MUIGrid>
          </MUICard>
        </MUIGrid>
      )}
    </MUIGrid>
  );
}
